import { useEffect, useState } from "react";
import { AppApiError } from "@/lib/api/apps/apps";
import { formatMoneyCents } from "@/lib/format";
import type {
  DraftProgress,
  ModelEntry,
  ProviderCatalog,
  ProviderEntry,
  ResearchOnTables,
  RunEvent,
  RunSummary,
} from "@/lib/api/apps/types";

export interface ModelChoice {
  provider: ProviderEntry;
  model: ModelEntry;
}

/** The providers a draft can run on: the trial while it lasts, and every
 *  provider the organization holds a key for. */
export function usableProviders(catalog: ProviderCatalog): ProviderEntry[] {
  return catalog.providers.filter(
    (p) => (p.key === "house" || p.key === "on_file") && p.models.length > 0,
  );
}

/** A provider's model by id, keyed or not: what an attempt ran on. */
export function findModel(
  catalog: ProviderCatalog,
  providerId: string,
  modelId: string,
): ModelChoice | null {
  const provider = catalog.providers.find((p) => p.id === providerId);
  const model = provider?.models.find((m) => m.id === modelId);
  return provider && model ? { provider, model } : null;
}

/** A recorded round's model by name. An id names one model whichever key paid
 *  for it, and the provider it ran on may no longer list it (the trial moved
 *  on; a non-personal org has no `house`). */
export function modelLabel(
  catalog: ProviderCatalog,
  providerId: string,
  modelId: string,
): string {
  return (
    findModel(catalog, providerId, modelId)?.model.name ??
    catalog.providers.flatMap((p) => p.models).find((m) => m.id === modelId)
      ?.name ??
    modelId
  );
}

/** The person's pick while it is still usable, else the catalog's default. */
export function chooseModel(
  catalog: ProviderCatalog,
  picked: { provider: string; model: string } | null,
): ModelChoice | null {
  const usable = usableProviders(catalog);
  const pool = { ...catalog, providers: usable };
  return (
    (picked && findModel(pool, picked.provider, picked.model)) ||
    findModel(pool, catalog.default_provider, catalog.default_model) ||
    (usable[0] ? { provider: usable[0], model: usable[0].models[0] } : null)
  );
}

export interface OrgRef {
  name: string;
  personal: boolean;
}

const whose = (org: OrgRef) => (org.personal ? "Your" : `${org.name}'s`);

/** "$1.20 of $3.00 used": the trial is the one allowance shown in dollars. */
export function trialLine(provider: ProviderEntry): string {
  const trial = provider.trial;
  return trial
    ? `${formatMoneyCents(trial.spent_cents, "USD")} of ${formatMoneyCents(trial.limit_cents, "USD")} used`
    : "";
}

/** Who pays for work on this choice. */
export function payerLine(choice: ModelChoice, org: OrgRef): string {
  return choice.provider.key === "house"
    ? `Trial on Mycorr's model, ${trialLine(choice.provider)}`
    : `${whose(org)} ${choice.provider.name} key ${choice.provider.hint ?? ""}`.trim();
}

/** What the person can change; drafting again as it was is the form's own button. */
export type DraftFix = "key" | "another";

export interface DraftFailure {
  title: string;
  /** The provider's or the agent's own words, shown as they came. */
  detail: string;
  fixes: DraftFix[];
}

/** A refused draft as the person reads it, with what each fix would be. */
export function draftFailure(
  error: unknown,
  choice: ModelChoice | null,
): DraftFailure {
  const code = error instanceof AppApiError ? error.code : "request_failed";
  const detail = error instanceof Error ? error.message : String(error);
  const provider = choice?.provider.name ?? "The provider";
  const model = choice?.model.name ?? "The model";
  const keyed = choice?.provider.key === "on_file";
  const refused = (title: string, fixes: DraftFix[]): DraftFailure => ({
    title,
    detail,
    fixes,
  });
  switch (code) {
    case "model_key_refused":
      return refused(
        `${provider} refused the key ${choice?.provider.hint ?? ""}`.trim(),
        ["key", "another"],
      );
    case "model_refused":
      return refused(
        `${provider} refused the request`,
        keyed ? ["key", "another"] : ["another"],
      );
    case "model_busy":
      return refused(`${model} is busy`, ["another"]);
    case "model_failed":
      return refused(`${model} failed`, ["another"]);
    case "step_crashed":
      return refused("The draft stopped unexpectedly three times", []);
    case "no_proposal":
      return refused("The agent returned no usable draft", ["another"]);
    case "trial_spent":
      return refused("Your trial on Mycorr's model is used up", [
        "key",
        "another",
      ]);
    case "cost_limit":
      return refused("The draft reached the app's cost limit", []);
    default:
      return refused("The draft could not start", []);
  }
}

export const clock = (ms: number) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

/** The wall clock, once a second while `running`. */
export function useNow(running: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return;
    const tick = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(tick);
  }, [running]);
  return now;
}

/** A design round's progress, as its panel shows it, read from the step that
 *  runs it: its lines, what its calls cost, and how it ended. */
export const DRAFT_POLL_MS = 1_000;

const tableList = new Intl.ListFormat("en", { type: "conjunction" });

/** Who is running research on which of the design's tables, and for how long. */
export function researchLine(run: ResearchOnTables): string {
  const who = run.by_you
    ? "You are running research"
    : run.owner_name
      ? `${run.owner_name} is running research`
      : "A research run is working";
  const minutes = run.minutes_left === 1 ? "minute" : "minutes";
  return `${who} on ${tableList.format(run.tables)}, for up to about ${run.minutes_left} more ${minutes}.`;
}

export function progressOf(run: RunSummary, events: RunEvent[]): DraftProgress {
  const started = Date.parse(run.started_at);
  const live = run.state === "queued" || run.state === "running";
  const steps = events
    .filter((e) => e.kind === "step" || e.kind === "warn")
    .map((e) => {
      const [text, ...detail] = e.summary.split(" · ");
      return {
        at_ms: Date.parse(e.at) - started,
        text,
        detail: detail.join(" · ") || undefined,
        tone: e.kind === "warn" ? ("warn" as const) : ("done" as const),
      };
    });
  const last = events[events.length - 1];
  const tokens = run.usage.reduce(
    (t, u) => ({
      in: t.in + u.input_tokens + u.cache_read_tokens + u.cache_write_tokens,
      out: t.out + u.output_tokens,
    }),
    { in: 0, out: 0 },
  );
  return {
    state: live ? "running" : run.state === "completed" ? "done" : "failed",
    started_at_ms: started,
    tokens_in: tokens.in,
    tokens_out: tokens.out,
    micros: run.spent_micros,
    steps,
    waiting_since_ms:
      run.state === "running"
        ? Date.parse(last?.at ?? run.started_at)
        : undefined,
    retry:
      live && last?.kind === "retry"
        ? { note: last.summary, at_ms: Date.parse(last.at) }
        : undefined,
    error:
      live || run.state === "completed"
        ? undefined
        : { code: run.end_reason ?? "model_failed", message: run.note ?? "" },
  };
}
